import Scrollbar from "@@/components/Scrollbar";
import TextInput from "@@/components/TextInput";
import Box from "@@/elements/Box";
import Typhography from "@@/elements/Typography";
import helpers from "@@/helpers/helpers";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { scroller } from "react-scroll";
import styles from "./styles.module.scss";

type PredictionType = "BRANCH" | "LOCALITY" | "PLACE";

type PredictionResult = {
  type: PredictionType;
  id?: string;
  description: string;
};

const BranchSearchInput = ({
  searchInput,
  setSearchInput,
  locations,
  setSelectedLocations,
  setSelectedLocality,
  setCurrentLocation,
  setMarkedLocation,
  setError,
  label,
  placeholder,
  branchHitLabel,
  localityHitLabel,
  placeHitLabel,
  suggestionsStatusLabel,
  clearSearchLabel,
  googleMapsAPICountryRestriction,
  googleMapsAPILanguage,
  isLoaded,
  error,
}) => {
  const [autoCompletePredictions, setAutoCompletePredictions] = useState<
    PredictionResult[]
  >([]);
  const [autoCompleteService, setAutoCompleteService] = useState(null);
  const [openAutocomplete, setOpenAutocomplete] = useState(true);
  const [activeAutocomplete, setActiveAutocomplete] = useState(true);
  const [fixed, setFixed] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [announceText, setAnnounceText] = useState("");
  const ref = useRef(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (isLoaded) {
      setAutoCompleteService(new google.maps.places.AutocompleteService());
    } else if (error) {
      setError("API_ERROR");
    }
  }, [isLoaded, error]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && ref.current.contains(event.target)) return;
      setOpenAutocomplete(false);
      setActiveIndex(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  const getMatchingLocalities = (input) => {
    const uniqueMatches = [];
    locations.forEach((location) => {
      if (
        location.locality &&
        location.locality.toLowerCase().startsWith(input.toLowerCase()) &&
        !uniqueMatches.find((match) => match.description === location.locality)
      ) {
        const uniqueMatch: PredictionResult = {
          description: location.locality,
          type: "LOCALITY",
        };
        uniqueMatches.push(uniqueMatch);
      }
    });
    return uniqueMatches;
  };

  const getMatchingBranches = (input) => {
    const matches = locations.filter(
      ({ name, locality, address, postalCode }) => {
        const nameKeys = name ? name.split(" ") : [];
        const localityKeys = locality ? locality.split(" ") : [];
        const addressKeys = address ? address.split(" ") : [];
        const postalCodeKeys = postalCode ? postalCode.split(" ").join("") : [];
        const locationValues = nameKeys.concat(
          localityKeys,
          addressKeys,
          postalCodeKeys,
        );
        const searchKeys = input.split(" ").filter((key) => key !== "");

        const isMatch = searchKeys.every((key) =>
          locationValues.some((value) =>
            value.toLowerCase().startsWith(key.toLowerCase()),
          ),
        );

        return isMatch;
      },
    );
    const matchingBranches = matches.map(
      (match): PredictionResult => ({
        id: match.id,
        description: match.name,
        type: "BRANCH",
      }),
    );
    return matchingBranches;
  };

  const getAutoCompleteValues = useCallback(async (input: string) => {
    if (!isLoaded || !autoCompleteService) return;

    const currentRequestId = ++requestIdRef.current;
    const matchingBranches = getMatchingBranches(input);
    const matchingLocalities = getMatchingLocalities(input);
    let predictedLocations: PredictionResult[] = [];

    try {
      const response = await autoCompleteService.getPlacePredictions({
        input,
        types: ["geocode"],
        language: googleMapsAPILanguage.trim(),
        componentRestrictions: { country: googleMapsAPICountryRestriction },
      });

      if (currentRequestId !== requestIdRef.current) return;

      if (response && response.predictions) {
        predictedLocations = response.predictions.map(
          (prediction): PredictionResult => ({
            id: prediction.place_id,
            description: prediction.description,
            type: "PLACE",
          }),
        );
      }
    } catch {
      if (currentRequestId !== requestIdRef.current) return;
    }

    if (currentRequestId !== requestIdRef.current) return;

    const matchingLocations = matchingBranches.concat(
      matchingLocalities,
      predictedLocations,
    );
    setAutoCompletePredictions(matchingLocations);
  }, [isLoaded, autoCompleteService, locations, googleMapsAPILanguage, googleMapsAPICountryRestriction]);

  const onClickAutoComplete = ({ id, description, type }: PredictionResult) => {
    setOpenAutocomplete(false);
    setActiveAutocomplete(false);
    setMarkedLocation(null);
    setSearchInput(description);
    setActiveIndex(null);
    setAnnounceText("");

    if (type === "BRANCH") {
      setSelectedLocations([id]);
      const selectedLocation = locations.find((location) => location.id === id);
      setMarkedLocation(selectedLocation);
    } else if (type === "LOCALITY") setSelectedLocality(description);
    else if (type === "PLACE") {
      new google.maps.places.PlacesService(
        document.createElement("div"),
      ).getDetails({ placeId: id }, (result, placestatus) => {
        if (placestatus === google.maps.places.PlacesServiceStatus.OK) {
          setCurrentLocation(result.geometry.location);
        } else {
          setError(placestatus);
        }
      });
    }
    setAutoCompletePredictions(null);
  };

  const onSubmit = () => {
    if (searchInput.length === 0) return;
    if (
      (activeAutocomplete && autoCompletePredictions === null) ||
      autoCompletePredictions === null
    )
      return;
    if (!autoCompletePredictions || !autoCompletePredictions.length)
      setError(google.maps.places.PlacesServiceStatus.ZERO_RESULTS);
    else {
      onClickAutoComplete(autoCompletePredictions[0]);
    }
  };

  const searchInputId = "branch-search-input";
  const listboxId = "branch-search-listbox";

  const hasVisibleSuggestions =
    !!autoCompletePredictions?.length && openAutocomplete;

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" && hasVisibleSuggestions) {
      e.preventDefault();
      setActiveIndex(0);
      listboxRef.current?.focus();
    }
    if (e.key === "Escape") {
      setOpenAutocomplete(false);
      setActiveIndex(null);
      setAnnounceText("");
    }
  };

  const onListboxKeyDown = (e: React.KeyboardEvent) => {
    if (!autoCompletePredictions?.length) return;
    const count = autoCompletePredictions.length;

    switch (e.key) {
    case "ArrowDown":
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % count);
      break;
    case "ArrowUp":
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + count) % count);
      break;
    case "Enter":
      e.preventDefault();
      if (activeIndex !== null) {
        onClickAutoComplete(autoCompletePredictions[activeIndex]);
        document.getElementById(searchInputId)?.focus();
      }
      break;
    case "Escape":
      e.preventDefault();
      setOpenAutocomplete(false);
      setActiveIndex(null);
      setAnnounceText("");
      document.getElementById(searchInputId)?.focus();
      break;
    }
  };

  useEffect(() => {
    if (activeIndex !== null) {
      document
        .getElementById(`branch-search-option-${activeIndex}`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (autoCompletePredictions?.length && openAutocomplete) {
      const count = autoCompletePredictions.length;
      const template =
        suggestionsStatusLabel ||
        "{count} suggestions available. Use up and down arrows to browse.";
      setAnnounceText(template.replace("{count}", String(count)));
    } else {
      setAnnounceText("");
    }
    setActiveIndex(null);
  }, [autoCompletePredictions, openAutocomplete]);

  const onClickInput = () => {
    setOpenAutocomplete(true);
    if (helpers.iOS()) {
      document.getElementById(searchInputId).focus();
      setTimeout(() => {
        scroller.scrollTo("branch-search", { duration: 500, smooth: true, offset: -100 });
      }, 400);
    } else {
      if (fixed && ref.current) {
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
        let timer = null;
        const focusInput = () => {
          if (timer !== null) {
            clearTimeout(timer);
          }
          timer = setTimeout(function () {
            document.getElementById(searchInputId).focus();
            window.removeEventListener("scroll", focusInput);
          }, 150);
        };
        window.addEventListener("scroll", focusInput, false);
      }
    }
  };

  useEffect(() => {
    if (searchInput && searchInput.length && activeAutocomplete) {
      setOpenAutocomplete(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        getAutoCompleteValues(searchInput);
      }, 200);
    } else if (activeAutocomplete) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestIdRef.current++;
      setAutoCompletePredictions(null);
      setSelectedLocations(null);
    }
    setActiveAutocomplete(true);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, getAutoCompleteValues]);

  useEffect(() => {
    const setFixedSearchInput = () => {
      const isVisible =
        ref.current.getBoundingClientRect().top - window.innerHeight + 126 < 0;
      setFixed(!isVisible);
    };
    setFixedSearchInput();
    window.addEventListener("scroll", setFixedSearchInput);
    return () => window.removeEventListener("scroll", setFixedSearchInput);
  }, [ref]);

  return (
    <Box
      ref={ref}
      id="branch-search"
      customStyle={{ scrollMarginTop: "50px" }}>
      <Box className={cn({ [styles.input__fixed]: fixed })}>
        <Typhography
          variant="label"
          mb={[2, 2, 3]}
          uppercase>
          {label}
        </Typhography>
        <button
          aria-label="search"
          className={cn(styles.scroll_button, {
            [styles.scroll_button__hidden]: !fixed,
          })}
          onClick={() => onClickInput()}
        />
        <div role="status" aria-live="polite" className={styles.sr_only}>
          {announceText}
        </div>
        <TextInput
          onSubmit={onSubmit}
          input={searchInput}
          setInput={setSearchInput}
          placeholder={placeholder}
          id={searchInputId}
          ariaAutoComplete="list"
          resetLabel={clearSearchLabel}
          onInputKeyDown={onInputKeyDown}
        />
        {hasVisibleSuggestions && (
          <div className={styles.autocomplete}>
            <div className={styles.autocomplete__list}>
              <Scrollbar style={{ maxHeight: "300px" }} tabindex={-1}>
                <div
                  ref={listboxRef}
                  role="listbox"
                  id={listboxId}
                  tabIndex={0}
                  aria-activedescendant={
                    activeIndex !== null
                      ? `branch-search-option-${activeIndex}`
                      : undefined
                  }
                  onKeyDown={onListboxKeyDown}
                  className={styles.autocomplete__listbox}
                >
                  {autoCompletePredictions.map(
                    ({ id, description, type }, i) => (
                      <div
                        key={i}
                        id={`branch-search-option-${i}`}
                        role="option"
                        tabIndex={-1}
                        aria-selected={activeIndex === i}
                        className={cn(
                          styles.autocomplete__list__option,
                          {
                            [styles.autocomplete__list__option_active]:
                              activeIndex === i,
                          },
                        )}
                        onClick={() =>
                          onClickAutoComplete({ id, description, type })
                        }
                      >
                        <Typhography variant="information">
                          {description}
                        </Typhography>
                        <Typhography
                          variant="information"
                          color="PrimaryGreyTint01"
                          className={styles.autocomplete__list__type}
                        >
                          {type === "BRANCH" && branchHitLabel}
                          {type === "LOCALITY" && localityHitLabel}
                          {type === "PLACE" && placeHitLabel}
                        </Typhography>
                      </div>
                    ),
                  )}
                </div>
              </Scrollbar>
            </div>
          </div>
        )}
      </Box>
    </Box>
  );
};

export default BranchSearchInput;
